import { Box, Tab, Tabs, Typography } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useUserPermissions } from "@/api/hooks/permissions";

const TABS = [
  { value: "therapeutic-areas", label: "Therapeutic areas" },
  { value: "document-requirements", label: "Document requirements" },
  { value: "milestones", label: "Milestones" },
  { value: "athena-mappings", label: "Athena mappings" },
] as const;

/**
 * Layout shell for the Administration sub-page. Renders a horizontal
 * tab strip mapped to the three editor routes; the active editor
 * lives in the <Outlet />. Non-admins land here only via direct URL
 * — we render a NotAuthorized notice rather than redirect, so the
 * URL stays stable and bookmarks survive a role grant.
 */
export function AdministrationLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = useUserPermissions();

  if (!permissions.isLoading && !permissions.data?.is_clinops_admin) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          ClinOps admin access required
        </Typography>
        <Typography color="text.secondary">
          Ask an administrator to add you to the watershed-clinops-admin group.
        </Typography>
      </Box>
    );
  }

  // The active tab key is the second URL segment under /clinops/administration.
  const segment = location.pathname.split("/").filter(Boolean)[2] ?? TABS[0].value;
  const active = TABS.find((t) => t.value === segment)?.value ?? TABS[0].value;

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Typography variant="h5" sx={{ px: 2, pt: 2, pb: 1, fontWeight: 600 }}>
          Administration
        </Typography>
        <Tabs
          value={active}
          onChange={(_, v) => navigate(v)}
          sx={{ px: 2 }}
        >
          {TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} sx={{ textTransform: "none" }} />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ px: 2 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
